import React, { useContext, useEffect, useState } from 'react';
import {
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControlLabel,
    Grid,
} from '@material-ui/core';

import { fbmApi } from 'api/fbm';
import { infoTechApi as api } from 'api/infoTech';
import { partnershipsApi } from 'api/partnerships';
import { warehouseApi } from 'api/warehouse';
import { UserContext } from 'contexts/UserContext';
import { DataTable, Notifications } from 'shared';

// The table to show roles
function ViewJalaRolesTable({ accountType, getAccounts, parentRow, setExpandedRow }) {
    const [rows, setRows] = useState([]);
    const [notification, setNotification] = useState({ text: null, level: null });
    const [deleteUserAccountModalOpen, setDeleteUserAccountModalOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [accountToRecreate, setAccountToRecreate] = useState(null);
    const { fbmAccount, user } = useContext(UserContext);

    const columns = [
        { accessor: 'id', Header: 'ID' },
        { accessor: 'description', Header: 'Role' },
    ];

    const getItemsInTable = async () => {
        const response = await api.getUserRoles(parentRow.original.id);

        if (response.status === false) {
            setNotification({ text: `No roles found ${response.msg}`, level: 'error' });
            setRows([]);
            return;
        }

        setRows(response.data.rows);
    };

    const recreateUserAccount = async () => {
        // Delete profiles
        const deleteProfilesResponse = await api.updateAccount({
            id: accountToRecreate.id,
            field: 'profiles',
            value: [],
        });
        if (deleteProfilesResponse.status === false) {
            let message = 'Error deleting profiles';
            if (deleteProfilesResponse.msg) {
                message += `: ${deleteProfilesResponse.msg}`;
            }
            setNotification({ text: message, level: 'error' });
            return;
        }

        const deleteParams = { authUserID: accountToRecreate.id };
        if (!user.fbm) {
            deleteParams.fbmAccount = fbmAccount.fbmAccountName;
        }
        const deleteUserAccountResponse = await fbmApi.deleteFBMUserAccount(deleteParams);
        if (deleteUserAccountResponse.status === false) {
            let message = 'Error deleting user account';
            if (deleteUserAccountResponse.msg) {
                message += `: ${deleteUserAccountResponse.msg}`;
            }
            setNotification({ text: message, level: 'error' });
            return;
        }

        // Recreate user account
        const recreatedUserAccountResponse = await api.createFoodAsMedicineAccount({
            email: accountToRecreate.email,
            firstName: accountToRecreate.firstName,
            lastName: accountToRecreate.lastName,
            orderSourceID: accountToRecreate.orderSourceID,
        });
        if (recreatedUserAccountResponse.status === false) {
            let message = 'Error recreating user account';
            if (recreatedUserAccountResponse.msg) {
                message += `: ${recreatedUserAccountResponse.msg}`;
            }
            setNotification({ text: message, level: 'error' });
            return;
        }

        const updatedAccounts = await api.getAccounts(false, accountType);
        if (updatedAccounts.status === false) {
            let message = 'Error getting updated accounts';
            if (updatedAccounts.msg) {
                message += `: ${updatedAccounts.msg}`;
            }
            setNotification({ text: message, level: 'error' });
            return;
        }

        const updatedAccount = updatedAccounts.data.rows.find((row) => row.email === accountToRecreate.email);
        if (!updatedAccount) {
            const message = 'Error finding updated account';
            setNotification({ text: message, level: 'error' });
            return;
        }

        // Recreate profiles
        const recreateProfilesResponse = await api.updateAccount({
            id: updatedAccount.id,
            field: 'profiles',
            value: accountToRecreate.profiles,
        });
        if (recreateProfilesResponse.status === false) {
            let message = 'Error recreating profiles';
            if (recreateProfilesResponse.msg) {
                message += `: ${recreateProfilesResponse.msg}`;
            }
            setNotification({ text: message, level: 'error' });
            return;
        }

        setNotification({ text: `User account ${accountToRecreate.email} recreated`, level: 'success' });
        setAccountToRecreate(null);
    };

    const handleClickOpen = () => {
        setAccountToRecreate(parentRow.original);
        setDeleteUserAccountModalOpen(true);
    };

    const handleClose = () => {
        setAccountToRecreate(null);
        getAccounts();
        setDeleteUserAccountModalOpen(false);
    };

    useEffect(() => {
        getItemsInTable();
    }, []);

    return (
        <div className="batch-table">
            <Notifications options={notification} />
            <DataTable columns={columns} data={rows} />
            {accountType === 'FOOD AS MEDICINE' && (
                <div style={{ textAlign: 'center', padding: '10px 0', margin: '10px 0' }}>
                    <Button variant="outlined" onClick={handleClickOpen}>
                        ⚠️Recreate User Account⚠️
                        <br />
                        (For user forgotten password or expired temporary password)
                    </Button>
                    <Dialog
                        open={deleteUserAccountModalOpen}
                        onClose={handleClose}
                        aria-labelledby="alert-dialog-title"
                        aria-describedby="alert-dialog-description"
                    >
                        <div>
                            <DialogTitle id="alert-dialog-title">
                                Recreate User Account: {accountToRecreate?.email}?
                            </DialogTitle>
                            {loading ? (
                                <div className="flex justify-center items-center h-full" style={{ padding: '20px' }}>
                                    <DialogContent
                                        id="alert-dialog-description"
                                        style={{ textAlign: 'center', padding: '10px 0' }}
                                    >
                                        Recreating User Account...
                                        <br />
                                        {accountToRecreate?.email}
                                        <br />
                                        Please wait, this may take a minute.
                                        <br />
                                        <br />
                                        <CircularProgress />
                                    </DialogContent>
                                </div>
                            ) : (
                                <div>
                                    <DialogContent>
                                        <DialogContentText id="alert-dialog-description">
                                            Are you sure you want to recreate the following user account?
                                            <br />
                                            {accountToRecreate?.email}
                                        </DialogContentText>
                                    </DialogContent>
                                    <DialogActions>
                                        <Button onClick={handleClose} disabled={loading}>
                                            Cancel
                                        </Button>
                                        <Button
                                            onClick={async () => {
                                                setLoading(true);
                                                await recreateUserAccount().finally(() => handleClose());
                                            }}
                                        >
                                            Recreate
                                        </Button>
                                    </DialogActions>
                                </div>
                            )}
                        </div>
                    </Dialog>
                </div>
            )}
        </div>
    );
}

function ViewJalapenoAccounts({ accountType = 'MISFITS MARKET' }) {
    const [rows, setRows] = useState([]);
    const [facilities, setFacilities] = useState([]);
    const [profiles, setProfiles] = useState([]);
    const [notification, setNotification] = useState({ text: null, level: null });
    const [activeOnly, setActiveOnly] = useState(true);
    const [famAccounts, setFamAccounts] = useState([]);

    // Load accounts
    const getAccounts = async () => {
        const response = await api.getAccounts(activeOnly, accountType);
        if (response.status === false) {
            setNotification({ text: `No entries found ${response.msg}`, level: 'error' });
            setRows([]);
            return;
        }

        if (response.data.rows) {
            response.data.rows.map((row) => {
                row.profilesWithNames = row.profiles;
                row.profiles = row.profiles.map((item) => item.id);
                return row;
            });
        }

        setRows(response.data.rows);
    };

    useEffect(() => {
        getAccounts();
    }, [activeOnly]);

    const getFamAccounts = async () => {
        const res = await partnershipsApi.getSources();
        if (!res.status) {
            return;
        }

        setFamAccounts(res.data.rows.map((row) => ({ text: row.name, value: row.id })));
    };

    const getFacilities = async () => {
        const response = await warehouseApi.getFacilities();
        if (!response.status) {
            setNotification({ text: 'Failed to get facilities', level: 'error' });
            return;
        }

        const facilitiesAsOpts = response.data.rows.map((row) => {
            row.text = row.name;
            row.value = row.id;
            row.disabled = !row.active;
            return row;
        });

        setFacilities(facilitiesAsOpts);
    };

    const getProfiles = async () => {
        const response = await api.getJalapenoProfiles(true, accountType);
        if (!response.status) {
            setNotification({ text: 'Failed to get profiles', level: 'error' });
            return;
        }

        const profileOpts = response.data.rows.map((row) => ({ text: row.name, value: row.id }));
        setProfiles(profileOpts);
    };

    useEffect(() => {
        getAccounts(true);
        getFacilities();
        getProfiles();
        getFamAccounts();
    }, []);

    const languages = [
        { text: 'English', value: 'EN' },
        { text: 'Spanish', value: 'ES' },
    ];

    const columns = [
        { accessor: 'id', Header: 'ID' },
        { accessor: 'email', Header: 'Email', editable: true },
        {
            accessor: 'orderSourceID',
            Header: 'FAM Account',
            // editable: true,
            editProps: { type: 'select', options: famAccounts },
            hide: accountType !== 'FOOD AS MEDICINE',
        },
        { accessor: 'firstName', Header: 'First Name', editable: true },
        { accessor: 'lastName', Header: 'Last Name', editable: true },
        { accessor: 'badgeNumber', Header: 'Badge Number', editable: true, hide: accountType !== 'MISFITS MARKET' },
        {
            accessor: 'language',
            Header: 'Language',
            editable: true,
            hide: accountType !== 'MISFITS MARKET',
            editProps: { type: 'select', options: languages },
        },
        {
            accessor: 'facilityID',
            Header: 'Facility',
            editable: true,
            editProps: { type: 'select', options: facilities },
            hide: accountType !== 'MISFITS MARKET',
        },
        {
            accessor: 'profiles',
            Header: 'Profiles',
            editable: true,
            filterValues: (row) => row.original.profilesWithNames.map((item) => item.name),
            editProps: {
                type: 'autocomplete-multiple',
                options: profiles,
                multiple: true,
            },
        },
    ];

    return (
        <div>
            <h1>{accountType === 'MISFITS MARKET' ? 'Jalapeno' : 'Food As Medicine'} Accounts</h1>
            <Notifications options={notification} />

            <div>
                <Grid item xs={12}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                id="ActiveOnly_checkbox"
                                onChange={(event) => {
                                    setActiveOnly(event.target.checked);
                                }}
                                color="primary"
                                defaultChecked
                            />
                        }
                        label="Active Only"
                    />
                </Grid>
                <DataTable
                    columns={columns}
                    data={rows}
                    editable
                    expandable
                    ExpansionComponent={<ViewJalaRolesTable accountType={accountType} getAccounts={getAccounts} />}
                    saveEdit={async ({ field, id, row, value }) => {
                        const res = await api.updateAccount({ id: row.id, field, value });
                        if (!res.status) {
                            setNotification({ text: res.msg, level: 'error' });
                            return false;
                        }

                        setNotification({ text: 'Account updated', level: 'success' });
                        getAccounts();
                        return true;
                    }}
                />
            </div>
        </div>
    );
}

export { ViewJalapenoAccounts };
